import { ButtonBase, styled, SxProps, Theme } from "@mui/material";
import { OverrideProps } from "@mui/material/OverridableComponent";
import { Children, ElementType, forwardRef, ReactElement, Ref } from "react";

export type SrIconButtonOwnProps = {
  variant?: "contained" | "outlined" | "text" | "checkbox";
  color?: "default" | "primary";
  size?: "small" | "medium" | "large" | "xl";
  shape?: "circle" | "square";
  // TODO: If this is used again, we may need a SrCheckbox component?
  checked?: boolean;
  sx?: SxProps<Theme>;
};

export interface SrIconButtonTypeMap<
  AdditionalProps = {},
  RootComponent extends ElementType = typeof ButtonBase,
> {
  props: AdditionalProps & SrIconButtonOwnProps;
  defaultComponent: RootComponent;
}

export type SrIconButtonProps<
  D extends ElementType = SrIconButtonTypeMap["defaultComponent"],
  P = {},
> = OverrideProps<SrIconButtonTypeMap<P, D>, D>;

export const srIconButtonClasses = {
  contained: "SrIconButton-contained",
  outlined: "SrIconButton-outlined",
  text: "SrIconButton-text",
  checkbox: "SrIconButton-checkbox",

  primary: "SrIconButton-primary",
  default: "SrIconButton-default",

  small: "SrIconButton-small",
  medium: "SrIconButton-medium",
  large: "SrIconButton-large",
  xl: "SrIconButton-xl",

  circle: "SrIconButton-circle",
  square: "SrIconButton-square",

  checked: "SrIconButton-checked",

  label: "SrIconButton-label",
} as const;

const SrIconButtonRoot = styled(ButtonBase)(({ theme }) => {
  return {
    gap: theme.spacing(1),
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    letterSpacing: 0.46,
    lineHeight: "20px",
    fontWeight: 600,

    "&:hover": {
      backgroundColor:
        "rgba(var(--sr-palette-action-activeChannel) / calc(var(--sr-palette-action-hoverOpacity)))",
      "--sr-palette-action-hoverOpacity": "0.1",
    },
    "&:active": {
      backgroundColor:
        "rgba(var(--sr-palette-action-activeChannel) / calc(var(--sr-palette-action-activeOpacity)))",
      "--sr-palette-action-activeOpacity": "0.15",
    },

    [`&.${srIconButtonClasses.small}`]: {
      height: 30,
      minWidth: 30,
      svg: { fontSize: 12 },
      [`&.${srIconButtonClasses.label}`]: {
        padding: `0 ${theme.spacing(1)}`,
      },
    },

    [`&.${srIconButtonClasses.medium}`]: {
      height: 36,
      minWidth: 36,
      svg: { fontSize: 14 },
      [`&.${srIconButtonClasses.label}`]: {
        padding: `0 ${theme.spacing(1.5)}`,
      },
    },

    [`&.${srIconButtonClasses.large}`]: {
      height: 42,
      minWidth: 42,
      svg: { fontSize: 16 },
      [`&.${srIconButtonClasses.label}`]: {
        padding: `0 ${theme.spacing(2)}`,
      },
    },

    [`&.${srIconButtonClasses.xl}`]: {
      height: 48,
      minWidth: 48,
      fontSize: 18,
      svg: { fontSize: 24 },
      [`&.${srIconButtonClasses.label}`]: {
        padding: `0 ${theme.spacing(2)}`,
      },
    },

    [`&.${srIconButtonClasses.circle}`]: {
      borderRadius: theme.shape.borderRadiusMax,
    },

    [`&.${srIconButtonClasses.square}`]: {
      borderRadius: theme.shape.borderRadius,
      [`&.${srIconButtonClasses.small}`]: {
        borderRadius: theme.shape.borderRadiusSm,
      },
    },

    [`&.${srIconButtonClasses.contained}`]: {
      [`&.${srIconButtonClasses.default}`]: {
        backgroundColor: theme.vars.palette.fg.A100,
        color: theme.vars.palette.text.primaryAlt,
      },
    },

    [`&.${srIconButtonClasses.text}`]: {
      [`&.${srIconButtonClasses.default}`]: {
        color: theme.vars.palette.text.primaryAlt,
      },
      [`&.${srIconButtonClasses.primary}`]: {
        color: theme.vars.palette.stroke.active,
      },
    },

    [`&.${srIconButtonClasses.outlined}`]: {
      border: "1px solid",
      [`&.${srIconButtonClasses.default}`]: {
        color: theme.vars.palette.text.secondary,
        borderColor: theme.vars.palette.stroke.main,
        "&:hover": {
          borderColor: theme.vars.palette.stroke.hover,
        },
        "&:active": {
          color: theme.vars.palette.text.primaryAlt,
        },
      },
      [`&.${srIconButtonClasses.primary}`]: {
        color: theme.vars.palette.stroke.active,
      },
    },

    [`&.${srIconButtonClasses.checkbox}:not(${srIconButtonClasses.checked})`]: {
      color: theme.vars.palette.text.secondary,
      "&:hover": { color: theme.vars.palette.text.primaryAlt },
    },

    [`&.${srIconButtonClasses.checkbox}.${srIconButtonClasses.checked}`]: {
      color: theme.vars.palette.primary.light,
      "&:hover": { color: theme.vars.palette.primary.dark },
    },
  };
});

export const SrIconButton = forwardRef(function IconButton<D extends ElementType>(
  {
    variant = "text",
    size = "medium",
    color = "default",
    shape = "square",
    checked = false,
    disabled,
    className,
    children,
    ...rest
  }: SrIconButtonProps<D>,
  ref: Ref<D>,
) {
  const classNames = [
    srIconButtonClasses[variant] ?? srIconButtonClasses.text,
    srIconButtonClasses[color] ?? srIconButtonClasses.default,
    srIconButtonClasses[size] ?? srIconButtonClasses.medium,
    srIconButtonClasses[shape] ?? srIconButtonClasses.square,
    checked ? srIconButtonClasses.checked : undefined,
    Children.toArray(children).filter(Boolean).length > 1 && srIconButtonClasses.label,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <SrIconButtonRoot
      ref={ref as Ref<HTMLButtonElement>}
      className={classNames}
      disabled={disabled}
      {...rest}
    >
      {children}
    </SrIconButtonRoot>
  );
}) as unknown as <D extends ElementType>(props: SrIconButtonProps<D>) => ReactElement;
